import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import axios from 'axios';
import { Plus, Edit, Trash2, X } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AdminBlog = () => {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingPost, setEditingPost] = useState(null);
  const [formData, setFormData] = useState({
    title: '',
    slug: '',
    content: '',
    excerpt: '',
    image: '',
    category: '',
    published: true,
  });

  useEffect(() => {
    fetchPosts();
  }, []);

  const fetchPosts = async () => {
    try {
      const token = localStorage.getItem('admin_token');
      const response = await axios.get(`${API}/blog`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setPosts(response.data);
    } catch (error) {
      console.error('Error fetching posts:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleOpenModal = (post = null) => {
    if (post) {
      setEditingPost(post);
      setFormData(post);
    } else {
      setEditingPost(null);
      setFormData({
        title: '',
        slug: '',
        content: '',
        excerpt: '',
        image: '',
        category: '',
        published: true,
      });
    }
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setEditingPost(null);
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData({ ...formData, [name]: type === 'checkbox' ? checked : value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const token = localStorage.getItem('admin_token');

    try {
      if (editingPost) {
        await axios.put(`${API}/blog/${editingPost.slug}`, formData, {
          headers: { Authorization: `Bearer ${token}` },
        });
        toast.success('Artículo actualizado');
      } else {
        await axios.post(`${API}/blog`, formData, {
          headers: { Authorization: `Bearer ${token}` },
        });
        toast.success('Artículo creado');
      }
      fetchPosts();
      handleCloseModal();
    } catch (error) {
      console.error('Error saving post:', error);
      toast.error(error.response?.data?.detail || 'Error al guardar artículo');
    }
  };

  const handleDelete = async (slug) => {
    if (!window.confirm('¿Estás seguro de eliminar este artículo?')) return;

    try {
      const token = localStorage.getItem('admin_token');
      await axios.delete(`${API}/blog/${slug}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success('Artículo eliminado');
      fetchPosts();
    } catch (error) {
      console.error('Error deleting post:', error);
      toast.error('Error al eliminar artículo');
    }
  };

  return (
    <div data-testid="admin-blog-page">
      <div className="flex justify-between items-center mb-8">
        <div>
          <h1 className="text-4xl font-bold tracking-tight mb-2">Blog</h1>
          <p className="text-sm font-sans tracking-widest uppercase text-ink/60">Gestionar Artículos</p>
        </div>
        <Button onClick={() => handleOpenModal()} className="bg-ink text-paper hover:bg-editorial" data-testid="add-post-button">
          <Plus className="w-4 h-4 mr-2" />
          Nuevo Artículo
        </Button>
      </div>

      {loading ? (
        <div className="text-center py-20">
          <p className="text-lg font-light text-ink/60">Cargando artículos...</p>
        </div>
      ) : posts.length === 0 ? (
        <div className="bg-paper p-12 editorial-border text-center">
          <p className="text-lg font-light text-ink/60 mb-4">No hay artículos creados</p>
          <Button onClick={() => handleOpenModal()} className="bg-ink text-paper hover:bg-editorial">
            Crear Primer Artículo
          </Button>
        </div>
      ) : (
        <div className="space-y-4">
          {posts.map((post) => (
            <motion.div
              key={post.id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="bg-paper editorial-border p-6 flex items-center gap-6"
              data-testid="post-item"
            >
              <img src={post.image} alt={post.title} className="w-32 h-32 object-cover" />
              <div className="flex-1">
                <h3 className="text-xl font-bold tracking-tight mb-2">{post.title}</h3>
                <p className="text-sm text-ink/60 mb-2">{post.category} • {new Date(post.date).toLocaleDateString('es-ES')}</p>
                <p className="text-sm text-ink/70">{post.excerpt}</p>
              </div>
              <div className="flex gap-2">
                <Button onClick={() => handleOpenModal(post)} variant="outline" size="sm" data-testid="edit-post-button">
                  <Edit className="w-4 h-4" />
                </Button>
                <Button onClick={() => handleDelete(post.slug)} variant="destructive" size="sm" data-testid="delete-post-button">
                  <Trash2 className="w-4 h-4" />
                </Button>
              </div>
            </motion.div>
          ))}
        </div>
      )}

      {showModal && (
        <div className="fixed inset-0 bg-ink/50 flex items-center justify-center z-50 p-6" data-testid="post-modal">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="bg-paper p-8 max-w-3xl w-full max-h-[90vh] overflow-y-auto editorial-border"
          >
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-2xl font-bold tracking-tight">{editingPost ? 'Editar Artículo' : 'Nuevo Artículo'}</h2>
              <button onClick={handleCloseModal} className="hover:text-editorial transition-colors">
                <X className="w-6 h-6" />
              </button>
            </div>

            <form onSubmit={handleSubmit} className="space-y-6">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="title">Título</Label>
                  <Input id="title" name="title" value={formData.title} onChange={handleChange} required data-testid="post-title-input" />
                </div>
                <div>
                  <Label htmlFor="slug">Slug</Label>
                  <Input id="slug" name="slug" value={formData.slug} onChange={handleChange} required data-testid="post-slug-input" />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="category">Categoría</Label>
                  <Input id="category" name="category" value={formData.category} onChange={handleChange} required data-testid="post-category-input" />
                </div>
                <div>
                  <Label htmlFor="image">URL de Imagen</Label>
                  <Input id="image" name="image" value={formData.image} onChange={handleChange} required data-testid="post-image-input" />
                </div>
              </div>

              <div>
                <Label htmlFor="excerpt">Extracto</Label>
                <Textarea id="excerpt" name="excerpt" value={formData.excerpt} onChange={handleChange} required rows={2} data-testid="post-excerpt-input" />
              </div>

              <div>
                <Label htmlFor="content">Contenido</Label>
                <Textarea id="content" name="content" value={formData.content} onChange={handleChange} required rows={12} data-testid="post-content-input" />
              </div>

              <div className="flex gap-4">
                <Button type="submit" className="flex-1 bg-ink text-paper hover:bg-editorial" data-testid="save-post-button">
                  {editingPost ? 'Actualizar' : 'Crear'} Artículo
                </Button>
                <Button type="button" onClick={handleCloseModal} variant="outline">
                  Cancelar
                </Button>
              </div>
            </form>
          </motion.div>
        </div>
      )}
    </div>
  );
};

export default AdminBlog;